import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Text, { TEXT_COLOR, TEXT_SIZE } from '@components/Text';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import { updateUserMfa } from '@containers/Admin/containers/UserPage/actions';
import { updateStaffUserMfa } from '@containers/Admin/containers/StaffPage/actions';

import {
  Wrapper,
  Title,
  Body,
  Buttons,
  OptionButton,
  CancelButton,
} from './styles';

const SelectMfaMethod = ({ userId, userEmail, phone, source, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const updateMfa = source === 'staff' ? updateStaffUserMfa : updateUserMfa;

  const titleText = useMemo(
    () => t('Authentication.MFA.selectTitle', 'Select MFA method'),
    [t],
  );

  const bodyText = useMemo(
    () => t('Authentication.MFA.selectBody', 'Choose how this user will receive MFA, or turn MFA off.'),
    [t],
  );

  const openConfirm = useCallback((method, options = {}) => {
    const normalized = String(method || '').trim().toUpperCase();
    const { warning = false } = options;
    const confirmTitle = t('Authentication.MFA.adminUpdateConfirmTitle', {
      email: userEmail || '',
      defaultValue: `Update MFA for "${userEmail || ''}"?`,
    });

    const confirmBody =
      warning
        ? t(
          'Authentication.MFA.emailWithoutPhoneWarning',
          'This user’s “Forgot Password” feature will not work without specifying a phone number.',
        )
        : normalized === 'EMAIL'
        ? t('Authentication.MFA.adminUpdateConfirmBodyEmail', 'This will require MFA via email for this user.')
        : t('Authentication.MFA.adminUpdateConfirmBodyOff', 'This will disable MFA for this user.');

    dispatch(openPopUp(POPUP_TYPES.CONFIRM, {
      titleText: confirmTitle,
      bodyText: confirmBody,
      confirmText: warning
        ? t('Authentication.MFA.continueWithoutPhone', 'Continue without phone number')
        : t('Actions.Confirmations.confirm', 'Confirm'),
      cancelText: t('Actions.Instance.cancel', 'Cancel'),
      disableBackdrop: true,
      confirmOnEnter: true,
      onConfirm: (confirm) => confirm && dispatch(updateMfa(userId, normalized)),
    }));
  }, [dispatch, t, updateMfa, userEmail, userId]);

  const onChooseSms = useCallback(() => {
    if (!phone) {
      dispatch(openPopUp(POPUP_TYPES.ALERT, {
        titleText: t('Authentication.MFA.phoneRequiredTitle', 'Phone number required'),
        bodyText: t(
          'Authentication.MFA.smsWithoutPhoneError',
          'SMS MFA cannot be used without a phone number defined. Please first specify a phone number for this user.',
        ),
        buttonText: t('Actions.Instance.close', 'Close'),
      }));
      return;
    }

    const confirmTitle = t('Authentication.MFA.adminUpdateConfirmTitle', {
      email: userEmail || '',
      defaultValue: `Update MFA for "${userEmail || ''}"?`,
    });
    dispatch(openPopUp(POPUP_TYPES.CONFIRM, {
      titleText: confirmTitle,
      bodyText: t('Authentication.MFA.adminUpdateConfirmBodySms', 'This will require MFA via SMS for this user.'),
      confirmText: t('Actions.Confirmations.confirm', 'Confirm'),
      cancelText: t('Actions.Instance.cancel', 'Cancel'),
      disableBackdrop: true,
      confirmOnEnter: true,
      onConfirm: (confirm) => confirm && dispatch(updateMfa(userId, 'SMS')),
    }));
  }, [dispatch, phone, t, updateMfa, userEmail, userId]);

  const onChooseEmail = useCallback(() => {
    openConfirm('EMAIL', { warning: !phone });
  }, [openConfirm, phone]);

  const onChooseOff = useCallback(() => {
    openConfirm('OFF');
  }, [openConfirm]);

  return (
    <Wrapper>
      <Title>
        <Text size={TEXT_SIZE.T0} textColor={TEXT_COLOR.DARK_BLUE} weight='900'>
          {titleText}
        </Text>
      </Title>

      <Body>
        <Text size={TEXT_SIZE.T3} textColor={TEXT_COLOR.DISABLED}>
          {bodyText}
        </Text>
      </Body>

      <Buttons>
        <OptionButton className='mfaSmsButton' onClick={onChooseSms}>
          {t('Authentication.MFA.optionSms', 'MFA SMS')}
        </OptionButton>
        <OptionButton className='mfaEmailButton' onClick={onChooseEmail}>
          {t('Authentication.MFA.optionEmail', 'MFA Email')}
        </OptionButton>
        <OptionButton className='mfaOffButton' onClick={onChooseOff}>
          {t('Authentication.MFA.optionOff', 'MFA Off')}
        </OptionButton>

        <CancelButton cancel className='cancelButton' onClick={onClose}>
          {t('Actions.Instance.cancel', 'Cancel')}
        </CancelButton>
      </Buttons>
    </Wrapper>
  );
};

SelectMfaMethod.propTypes = {
  userId: PropTypes.string,
  userEmail: PropTypes.string,
  phone: PropTypes.string,
  source: PropTypes.string,
  onClose: PropTypes.func,
};

export default SelectMfaMethod;
